/**
 * Action-Level Permission Matrix
 * 
 * Granular action authority for every role across all major modules.
 * This is the final permission layer on top of menu visibility, route access, and module permissions.
 * 
 * Vocabulary: view | interact | create | edit | approve | override | delete
 */
import type { UserRole } from "./RoleContext";

// ── Action Keys by Domain ──

export type SalesAction =
  | "create_lead" | "edit_lead" | "delete_lead"
  | "send_message" | "create_followup" | "book_appointment"
  | "reschedule_appointment" | "start_consultation"
  | "result_appointment" | "edit_submitted_result" | "override_submitted_result"
  | "create_opportunity" | "edit_opportunity_stage";

export type ResultsAction =
  | "view_results" | "submit_result"
  | "edit_locked_result" | "override_result"
  | "view_scorecards" | "view_commissions"
  | "approve_commissions" | "override_commissions";

export type OperationsAction =
  | "create_job" | "edit_job" | "schedule_job" | "dispatch_job"
  | "change_install_readiness" | "closeout_project"
  | "open_issue" | "resolve_issue" | "delete_issue";

export type MaterialsAction =
  | "create_order" | "edit_order" | "receive_inventory"
  | "allocate_inventory" | "mark_staged_loaded"
  | "edit_vendor_records";

export type FinanceAction =
  | "edit_accounting_records" | "view_revenue_intelligence"
  | "approve_payouts" | "override_commission_adjustments"
  | "record_payment" | "create_credit";

export type AdminAction =
  | "change_navigation_settings" | "access_control_plane"
  | "run_demo_tools" | "override_protected_states"
  | "edit_catalog" | "manage_users";

export type ActionKey =
  | SalesAction | ResultsAction | OperationsAction
  | MaterialsAction | FinanceAction | AdminAction;

// ── Matrix Definition ──

type ActionMatrix = Record<UserRole, Set<ActionKey>>;

const SA: ActionKey[] = [
  // All actions
  "create_lead", "edit_lead", "delete_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment", "start_consultation",
  "result_appointment", "edit_submitted_result", "override_submitted_result",
  "create_opportunity", "edit_opportunity_stage",
  "view_results", "submit_result", "edit_locked_result", "override_result",
  "view_scorecards", "view_commissions", "approve_commissions", "override_commissions",
  "create_job", "edit_job", "schedule_job", "dispatch_job",
  "change_install_readiness", "closeout_project",
  "open_issue", "resolve_issue", "delete_issue",
  "create_order", "edit_order", "receive_inventory", "allocate_inventory",
  "mark_staged_loaded", "edit_vendor_records",
  "edit_accounting_records", "view_revenue_intelligence",
  "approve_payouts", "override_commission_adjustments",
  "record_payment", "create_credit",
  "change_navigation_settings", "access_control_plane",
  "run_demo_tools", "override_protected_states", "edit_catalog", "manage_users",
];

const OWNER: ActionKey[] = [
  "create_lead", "edit_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment", "start_consultation",
  "result_appointment", "override_submitted_result",
  "create_opportunity", "edit_opportunity_stage",
  "view_results", "submit_result", "override_result",
  "view_scorecards", "view_commissions", "approve_commissions", "override_commissions",
  "create_job", "edit_job", "schedule_job", "dispatch_job",
  "change_install_readiness", "closeout_project",
  "open_issue", "resolve_issue",
  "create_order", "edit_order", "receive_inventory", "allocate_inventory",
  "mark_staged_loaded", "edit_vendor_records",
  "edit_accounting_records", "view_revenue_intelligence",
  "approve_payouts", "override_commission_adjustments",
  "record_payment", "create_credit",
  "change_navigation_settings", "override_protected_states", "edit_catalog", "manage_users",
];

const OPS_MANAGER: ActionKey[] = [
  "create_lead", "edit_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment",
  "create_opportunity", "edit_opportunity_stage",
  "view_results", "view_scorecards",
  "create_job", "edit_job", "schedule_job", "dispatch_job",
  "change_install_readiness", "closeout_project",
  "open_issue", "resolve_issue",
  "create_order", "edit_order",
  "view_revenue_intelligence",
];

const ADMIN: ActionKey[] = [
  "create_lead", "edit_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment",
  "create_opportunity", "edit_opportunity_stage",
  "view_results", "view_scorecards",
  "create_job", "edit_job", "schedule_job",
  "change_install_readiness", "closeout_project",
  "open_issue", "resolve_issue",
  "create_order", "edit_order",
  "edit_vendor_records",
  "change_navigation_settings", "edit_catalog",
];

const SALES_MANAGER: ActionKey[] = [
  "create_lead", "edit_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment", "start_consultation",
  "result_appointment", "override_submitted_result",
  "create_opportunity", "edit_opportunity_stage",
  "view_results", "submit_result", "override_result",
  "view_scorecards", "view_commissions",
  "open_issue",
];

const SALES: ActionKey[] = [
  "create_lead", "edit_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment", "start_consultation",
  "result_appointment",
  "create_opportunity", "edit_opportunity_stage",
  "view_results", "submit_result",
  "view_scorecards", "view_commissions",
  "open_issue",
];

const CALL_CENTER: ActionKey[] = [
  "create_lead", "edit_lead", "send_message", "create_followup",
  "book_appointment", "reschedule_appointment",
  "view_results",
];

const CFO: ActionKey[] = [
  "view_results", "view_scorecards", "view_commissions",
  "approve_commissions", "override_commissions",
  "edit_accounting_records", "view_revenue_intelligence",
  "approve_payouts", "override_commission_adjustments",
  "record_payment", "create_credit",
];

const FINANCE_MANAGER: ActionKey[] = [
  "view_results", "view_scorecards", "view_commissions",
  "approve_commissions",
  "edit_accounting_records", "view_revenue_intelligence",
  "approve_payouts",
  "record_payment", "create_credit",
];

const BOOKKEEPER: ActionKey[] = [
  "view_results", "view_commissions",
  "edit_accounting_records",
  "record_payment", "create_credit",
];

const WAREHOUSE_MANAGER: ActionKey[] = [
  "receive_inventory", "allocate_inventory", "mark_staged_loaded",
  "open_issue",
  "create_order", "edit_order",
];

const HEAD_OF_INSTALLATION: ActionKey[] = [
  "schedule_job", "dispatch_job",
  "change_install_readiness",
  "open_issue", "resolve_issue",
  "create_order",
  "allocate_inventory",
];

const PROCUREMENT_MANAGER: ActionKey[] = [
  "create_order", "edit_order",
  "edit_vendor_records",
  "receive_inventory",
];

const INVENTORY_MANAGER: ActionKey[] = [
  "receive_inventory", "allocate_inventory", "mark_staged_loaded",
  "create_order",
];

const INSTALLER: ActionKey[] = [
  "open_issue",
  "view_results",
];

const SUBCONTRACTOR: ActionKey[] = [
  "open_issue",
  "view_results",
];

// ── Build Matrix ──

const buildMatrix = (): ActionMatrix => {
  const m: Partial<ActionMatrix> = {};
  const entries: [UserRole, ActionKey[]][] = [
    ["super_admin", SA],
    ["owner", OWNER],
    ["ops_manager", OPS_MANAGER],
    ["admin", ADMIN],
    ["sales_manager", SALES_MANAGER],
    ["sales", SALES],
    ["call_center", CALL_CENTER],
    ["cfo", CFO],
    ["finance_manager", FINANCE_MANAGER],
    ["bookkeeper", BOOKKEEPER],
    ["warehouse_manager", WAREHOUSE_MANAGER],
    ["head_of_installation", HEAD_OF_INSTALLATION],
    ["procurement_manager", PROCUREMENT_MANAGER],
    ["inventory_manager", INVENTORY_MANAGER],
    ["installer", INSTALLER],
    ["subcontractor", SUBCONTRACTOR],
    ["it_staff", ADMIN],
  ];
  for (const [role, actions] of entries) {
    m[role] = new Set(actions);
  }
  return m as ActionMatrix;
};

const ACTION_MATRIX = buildMatrix();

// ── Public API ──

/** Check if a role is allowed to perform a specific action */
export function canPerformAction(role: UserRole, action: ActionKey): boolean {
  return ACTION_MATRIX[role]?.has(action) ?? false;
}

/** Get all allowed actions for a role */
export function getAllowedActions(role: UserRole): ActionKey[] {
  return Array.from(ACTION_MATRIX[role] ?? []);
}

/** Get a human-readable restriction reason for a denied action */
export function getActionRestriction(role: UserRole, action: ActionKey): string | null {
  if (canPerformAction(role, action)) return null;
  return ACTION_RESTRICTION_REASONS[action] ?? `This action requires higher authorization than the ${role.replace(/_/g, " ")} role.`;
}

// ── Restriction Reasons ──

const ACTION_RESTRICTION_REASONS: Partial<Record<ActionKey, string>> = {
  edit_submitted_result: "Submitted results are locked. Admin or Sales Manager override required.",
  override_submitted_result: "Only Sales Manager, Owner, or Super Admin can override submitted results.",
  edit_locked_result: "Locked results cannot be edited. Contact an administrator.",
  override_result: "Only Sales Manager, Owner, or Super Admin can override results.",
  approve_commissions: "Only Finance, CFO, Owner, or Super Admin can approve commissions.",
  override_commissions: "Only CFO, Owner, or Super Admin can override commissions.",
  override_commission_adjustments: "Only CFO, Owner, or Super Admin can override commission adjustments.",
  approve_payouts: "Only Finance, CFO, Owner, or Super Admin can approve payouts.",
  edit_accounting_records: "Only Finance roles can edit accounting records.",
  override_protected_states: "Only Owner or Super Admin can override protected states.",
  access_control_plane: "Control Plane access is restricted to Super Admin.",
  change_navigation_settings: "Navigation settings require Admin, Owner, or Super Admin access.",
  run_demo_tools: "Demo tools are restricted to Super Admin.",
  manage_users: "User management requires Owner or Super Admin access.",
  delete_lead: "Only Super Admin can delete leads.",
  delete_issue: "Only Super Admin can delete issues.",
  dispatch_job: "Only Ops Manager, Head of Install, Owner, or Super Admin can dispatch jobs.",
  closeout_project: "Only Ops Manager, Admin, Owner, or Super Admin can close out projects.",
  edit_vendor_records: "Only Admin, Procurement, Owner, or Super Admin can edit vendor records.",
  create_credit: "Only Finance roles, Owner, or Super Admin can create credits.",
  record_payment: "Only Finance roles, Owner, or Super Admin can record payments.",
  start_consultation: "Only Sales, Sales Manager, Owner, or Super Admin can start consultations.",
  result_appointment: "Only Sales, Sales Manager, Owner, or Super Admin can result appointments.",
};

// ── Convenience Guards ──
// Named helpers for the most common checks across the app.

export const canCreateLead = (role: UserRole) => canPerformAction(role, "create_lead");
export const canEditLead = (role: UserRole) => canPerformAction(role, "edit_lead");
export const canDeleteLead = (role: UserRole) => canPerformAction(role, "delete_lead");
export const canBookAppointment = (role: UserRole) => canPerformAction(role, "book_appointment");
export const canRescheduleAppointment = (role: UserRole) => canPerformAction(role, "reschedule_appointment");
export const canStartConsultation = (role: UserRole) => canPerformAction(role, "start_consultation");
export const canResultAppointment = (role: UserRole) => canPerformAction(role, "result_appointment");
export const canEditSubmittedResult = (role: UserRole) => canPerformAction(role, "edit_submitted_result");
export const canOverrideSubmittedResult = (role: UserRole) => canPerformAction(role, "override_submitted_result");
export const canCreateOpportunity = (role: UserRole) => canPerformAction(role, "create_opportunity");
export const canEditOpportunityStage = (role: UserRole) => canPerformAction(role, "edit_opportunity_stage");

export const canSubmitResult = (role: UserRole) => canPerformAction(role, "submit_result");
export const canOverrideResult = (role: UserRole) => canPerformAction(role, "override_result");
export const canViewScorecards = (role: UserRole) => canPerformAction(role, "view_scorecards");
export const canViewCommissions = (role: UserRole) => canPerformAction(role, "view_commissions");
export const canApproveCommissions = (role: UserRole) => canPerformAction(role, "approve_commissions");
export const canOverrideCommissions = (role: UserRole) => canPerformAction(role, "override_commissions");

export const canCreateJob = (role: UserRole) => canPerformAction(role, "create_job");
export const canEditJob = (role: UserRole) => canPerformAction(role, "edit_job");
export const canScheduleJob = (role: UserRole) => canPerformAction(role, "schedule_job");
export const canDispatchJob = (role: UserRole) => canPerformAction(role, "dispatch_job");
export const canChangeInstallReadiness = (role: UserRole) => canPerformAction(role, "change_install_readiness");
export const canCloseoutProject = (role: UserRole) => canPerformAction(role, "closeout_project");
export const canOpenIssue = (role: UserRole) => canPerformAction(role, "open_issue");
export const canResolveIssue = (role: UserRole) => canPerformAction(role, "resolve_issue");

export const canCreateOrder = (role: UserRole) => canPerformAction(role, "create_order");
export const canEditOrder = (role: UserRole) => canPerformAction(role, "edit_order");
export const canReceiveInventory = (role: UserRole) => canPerformAction(role, "receive_inventory");
export const canAllocateInventory = (role: UserRole) => canPerformAction(role, "allocate_inventory");
export const canMarkStagedLoaded = (role: UserRole) => canPerformAction(role, "mark_staged_loaded");
export const canEditVendorRecords = (role: UserRole) => canPerformAction(role, "edit_vendor_records");

export const canEditAccounting = (role: UserRole) => canPerformAction(role, "edit_accounting_records");
export const canViewRevenueIntelligence = (role: UserRole) => canPerformAction(role, "view_revenue_intelligence");
export const canApprovePayouts = (role: UserRole) => canPerformAction(role, "approve_payouts");
export const canRecordPayment = (role: UserRole) => canPerformAction(role, "record_payment");
export const canCreateCredit = (role: UserRole) => canPerformAction(role, "create_credit");

export const canChangeNavSettings = (role: UserRole) => canPerformAction(role, "change_navigation_settings");
export const canAccessControlPlane = (role: UserRole) => canPerformAction(role, "access_control_plane");
export const canRunDemoTools = (role: UserRole) => canPerformAction(role, "run_demo_tools");
export const canOverrideProtectedStates = (role: UserRole) => canPerformAction(role, "override_protected_states");
export const canEditCatalog = (role: UserRole) => canPerformAction(role, "edit_catalog");
export const canManageUsers = (role: UserRole) => canPerformAction(role, "manage_users");

// ── Immutable / Sensitive Action Registry ──

export const SENSITIVE_ACTIONS: ActionKey[] = [
  "edit_submitted_result",
  "override_submitted_result",
  "edit_locked_result",
  "override_result",
  "approve_commissions",
  "override_commissions",
  "override_commission_adjustments",
  "approve_payouts",
  "override_protected_states",
  "access_control_plane",
  "delete_lead",
  "delete_issue",
  "manage_users",
];

/** Check if an action is classified as sensitive (requires audit logging) */
export function isSensitiveAction(action: ActionKey): boolean {
  return SENSITIVE_ACTIONS.includes(action);
}

// ── Override Rules ──

export const OVERRIDE_ELIGIBLE_ROLES: Partial<Record<ActionKey, UserRole[]>> = {
  override_submitted_result: ["super_admin", "owner", "sales_manager"],
  override_result: ["super_admin", "owner", "sales_manager"],
  override_commissions: ["super_admin", "owner", "cfo"],
  override_commission_adjustments: ["super_admin", "owner", "cfo"],
  override_protected_states: ["super_admin", "owner"],
};

/** Check if a role can override a specific action */
export function canOverride(role: UserRole, action: ActionKey): boolean {
  const eligible = OVERRIDE_ELIGIBLE_ROLES[action];
  if (!eligible) return false;
  return eligible.includes(role);
}
